import Graphic from "@arcgis/core/Graphic";
import Point from "@arcgis/core/geometry/Point";
import * as distanceOperator from "@arcgis/core/geometry/operators/distanceOperator.js";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { getElevationOfFeature, getMapView } from "./arcgisUtils";
import Polyline from "@arcgis/core/geometry/Polyline";
import FeatureSet from "@arcgis/core/rest/support/FeatureSet.js";

/**
 * Calculate the sensitivity(Godhetstall) of the overrett
 * @param lowerOverett The lower overett height over terrain
 * @param lowerOverettTerrainElevation The lower overett terrain elevation
 * @param upperOverett The upper overett height over terrain
 * @param upperOverettTerrainElevation The upper overett terrain elevation
 * @param observationHeight The observation height
 * @param upperWorkingAreaLen The distance between upper and lower overett
 * @param lowerWorkingAreaLen The distance between the observer and the lower overett
 *
 */
export const calculateSensitivity = (
    lowerOverett: number,
    lowerOverettTerrainElevation: number,
    upperOverett: number,
    upperOverettTerrainElevation: number,
    waterWidth: number,
    upperWorkingAreaLen: number,
    lowerWorkingAreaLen: number
) => {
    const lowerHeight = lowerOverett + lowerOverettTerrainElevation;
    const upperHeight = upperOverett + upperOverettTerrainElevation;

    const sensitivity = sensitivityCalc(upperHeight, lowerWorkingAreaLen, upperWorkingAreaLen, lowerHeight, waterWidth);

    return sensitivity;
};

/**
 * Calculate the vertical angle of the overrett
 * @param lowerOverett The lower overett height over terrain
 * @param lowerOverettTerrainElevation The lower overett terrain elevation
 * @param upperOverett The upper overett height over terrain
 * @param upperOverettTerrainElevation The upper overett terrain elevation
 * @param observationHeight The observation height
 * @param upperWorkingAreaLen The distance between upper and lower overett
 * @param lowerWorkingAreaLen The distance between the observer and the lower overett
 * @returns
 */
export const calculateAngle = (
    lowerOverett: number,
    lowerOverettTerrainElevation: number,
    upperOverett: number,
    upperOverettTerrainElevation: number,
    observationHeight: number,
    upperWorkingAreaLen: number,
    lowerWorkingAreaLen: number
) => {
    const lowerHeight = lowerOverett + lowerOverettTerrainElevation;
    const upperHeight = upperOverett + upperOverettTerrainElevation;

    const angle = angleCalc(upperHeight, lowerWorkingAreaLen, upperWorkingAreaLen, lowerHeight, observationHeight);

    return angle;
};

/**
 * Calculate the distance between the upper overett and the lower overett in meters
 * @param upperOverett Point object representing the upper overett
 * @param upperOverettHeightOverTerrain The height of the upper overett over the terrain
 * @param lowerOverett Point object representing the lower overett
 * @param lowerOverettHeightOverTerrain The height of the lower overett over the terrain
 * @returns
 */
export const distanceBetweenOverett = (
    upperOverett: Point,
    upperOverettHeightOverTerrain: number,
    lowerOverett: Point,
    lowerOverettHeightOverTerrain: number
) => {
    let upperHeight = upperOverettHeightOverTerrain;
    let lowerHeight = lowerOverettHeightOverTerrain;

    if (upperOverett.hasZ) {
        upperHeight = upperOverettHeightOverTerrain + (upperOverett.z ?? 0);
    }
    if (lowerOverett.hasZ) {
        lowerHeight = lowerOverettHeightOverTerrain + (lowerOverett.z ?? 0);
    }

    const deltaHeight = Math.abs(upperHeight - lowerHeight);
    const distance = distanceOperator.execute(upperOverett, lowerOverett, { unit: "meters" });
    // Calculate the actual distance between the overrett
    // The distance between the overrett is the hypotenuse of the triangle
    const distanceBetweenObjects = Math.sqrt(Math.pow(distance, 2) + Math.pow(deltaHeight, 2));

    return distanceBetweenObjects;
};

/**
 * Calculate the distance between the lower overett and the observer in meters
 * @param lowerOverett Point object representing the lower overett
 * @param lowerOverettHeightOverTerrain The height of the lower overett over the terrain
 * @param observer Point object representing the observer
 * @param observerHeight The height of the observer
 * @returns
 */
export const distanceBetweenLowerOverettAndObserver = (
    lowerOverett: Point,
    lowerOverettHeightOverTerrain: number,
    observer: Point,
    observerHeight: number
) => {
    let lowerHeight = lowerOverettHeightOverTerrain;
    let observerHeightOverTerrain = observerHeight;

    if (lowerOverett.hasZ) {
        lowerHeight = lowerOverettHeightOverTerrain + (lowerOverett.z ?? 0);
    }
    if (observer.hasZ) {
        observerHeightOverTerrain = observerHeight + (observer.z ?? 0);
    }

    const deltaHeight = Math.abs(observerHeightOverTerrain - lowerHeight);
    const distance = distanceOperator.execute(lowerOverett, observer, { unit: "meters" });
    // Calculate the actual distance between the overrett
    // The distance between the overrett is the hypotenuse of the triangle
    const distanceBetweenOverett = Math.sqrt(Math.pow(distance, 2) + Math.pow(deltaHeight, 2));

    return distanceBetweenOverett;
};

/**
 * Calculate the distance between the observer and the upper overett in meters
 * @param upperOverett Point object representing the upper overett
 * @param upperOverettHeightOverTerrain The height of the upper overett over the terrain
 * @param observer Point object representing the observer
 * @param observerHeight The height of the observer
 * @returns
 */
const sensitivityCalc = (
    upperHeight: number,
    lowerWorkingAreaLen: number,
    upperWorkingAreaLen: number,
    lowerHeight: number,
    waterWidth: number
) => {
    // This formula is copied from the existing solution. It is not 100% clear how it is derived
    return waterWidth * (upperWorkingAreaLen / (lowerWorkingAreaLen * (upperHeight - lowerHeight)));
};

/**
 * Calculate the vertical angle of the overrett
 * @param upperHeight The height of the upper overett
 * @param lowerWorkingAreaLen The distance between the observer and the lower overett
 * @param upperWorkingAreaLen The distance between the upper and lower overett
 * @param lowerHeight The height of the lower overett
 * @param observationHeight The observation height
 * @returns
 */
const angleCalc = (
    upperHeight: number,
    lowerWorkingAreaLen: number,
    upperWorkingAreaLen: number,
    lowerHeight: number,
    observationHeight: number
) => {
    // This formula is copied from the existing solution. It is not 100% clear how it is derived
    // 3438 connected to describing and angle in arc minutes
    return (
        3438 *
        ((upperHeight - observationHeight) / (lowerWorkingAreaLen + upperWorkingAreaLen) -
            (lowerHeight - observationHeight) / lowerWorkingAreaLen)
    );
};

const getOverettLinje = async (feature: Graphic) => {
    const layer = feature.layer as FeatureLayer;

    if (!layer) return {};

    if (layer?.id === "Overettlinje lys" || layer?.id === "Overettlinje merke") {
        return {
            [feature.attributes.objectid]: new FeatureSet({
                features: [feature],
                geometryType: "polyline",
                spatialReference: layer.spatialReference,
                fields: layer.fields,
            }),
        };
    }

    const relationships = layer.relationships;
    const relationshipName = layer.id === "Lys" ? "OverettlinjeLys" : "OverettLinje";
    const relationshipName2 = layer.id === "Lys" ? "OverettlinjeLys2" : "OverettlinjeMerke2";
    const relationshipId = relationships?.find((relationship) => relationship.name === relationshipName)?.id;
    const relationshipId2 = relationships?.find((relationship) => relationship.name === relationshipName2)?.id;

    if (relationshipId === undefined && relationshipId2 === undefined) return {};

    let results = {};
    if (relationshipId !== undefined) {
        results = await layer.queryRelatedFeatures({
            relationshipId: relationshipId,
            objectIds: [feature.attributes.objectid],
            returnGeometry: true,
            geometryPrecision: 20,
            outFields: ["*"],
        });
    }
    let results2 = {};
    if (relationshipId2 !== undefined) {
        results2 = await layer.queryRelatedFeatures({
            relationshipId: relationshipId2,
            objectIds: [feature.attributes.objectid],
            returnGeometry: true,
            geometryPrecision: 20,
            outFields: ["*"],
        });
    }

    return { ...results, ...results2 };
};

export const hasAttachedOverettLinje = async (feature: Graphic) => {
    const results = await getOverettLinje(feature);
    return Object.keys(results).length > 0;
};

export const getOverettFeatures = async (feature: Graphic) => {
    const mapView = getMapView();
    if (!mapView?.map) return;

    const overettLinje = await getOverettLinje(feature);

    let lineLayerId;
    let objectLayerId;

    switch (feature.layer?.id) {
        case "Lys":
        case "Overettlinje lys":
            lineLayerId = "Overettlinje lys";
            objectLayerId = "Lys";
            break;
        case "Fast sjømerke":
        case "Overettlinje merke":
            lineLayerId = "Overettlinje merke";
            objectLayerId = "Fast sjømerke";
            break;
        default:
            return;
    }

    const lineLayer = mapView.map.findLayerById(lineLayerId) as FeatureLayer;
    const objectLayer = mapView.map.findLayerById(objectLayerId) as FeatureLayer;

    const objectids = Object.values(overettLinje)[0].features.map((feature) => feature.attributes.objectid);

    let relationshipName1;
    let relationshipName2;

    if (feature.layer.id === "Lys" || feature.layer.id === "Overettlinje lys") {
        relationshipName1 = "Lys";
        relationshipName2 = "Lys2";
    } else if (feature.layer.id === "Fast sjømerke" || feature.layer.id === "Overettlinje merke") {
        relationshipName1 = "FastSjoMerke";
        relationshipName2 = "FastSjomerke2";
    } else {
        return;
    }

    const object1 = await lineLayer.queryRelatedFeatures({
        relationshipId: lineLayer.relationships?.find((relationship) => relationship.name === relationshipName1)?.id,
        objectIds: objectids,
        outFields: ["*"],
        returnGeometry: true,
        geometryPrecision: 20,
    });

    const object2 = await lineLayer.queryRelatedFeatures({
        relationshipId: lineLayer.relationships?.find((relationship) => relationship.name === relationshipName2)?.id,
        objectIds: objectids,
        outFields: ["*"],
        returnGeometry: true,
        geometryPrecision: 20,
    });

    const featureSets = [Object.values(overettLinje)[0], Object.values(object1)[0], Object.values(object2)[0]].filter(
        (f) => f !== undefined
    );
    const features = featureSets.flatMap((featureSet) => featureSet?.features[0]);

    features[0].layer = lineLayer;
    (features[0] as any).sourceLayer = lineLayer;
    if (features[1]) {
        features[1].layer = objectLayer;
        (features[1] as any).sourceLayer = objectLayer;
    }
    if (features[2]) {
        features[2].layer = objectLayer;
        (features[2] as any).sourceLayer = objectLayer;
    }

    return features;
};

export const getOverettElevations = async (
    upperOverett: Graphic,
    lowerOverett: Graphic
): Promise<[Graphic, Graphic, number, number] | null> => {
    if (!upperOverett || !lowerOverett) return null;
    const mapView = getMapView();
    if (!mapView) return null;

    const upperElevation = (await getElevationOfFeature(upperOverett)) ?? 0;
    const lowerElevation = (await getElevationOfFeature(lowerOverett)) ?? 0;

    if (upperOverett?.geometry) {
        upperOverett.geometry.spatialReference = mapView.spatialReference;
    }
    if (lowerOverett?.geometry) {
        lowerOverett.geometry.spatialReference = mapView.spatialReference;
    }

    return [upperOverett, lowerOverett, upperElevation, lowerElevation];
};

export const getOverettLinjeEndPoint = (overettLinje: Graphic, overettUpper: Point) => {
    if (!overettLinje || !overettUpper) return;

    const mapView = getMapView();
    if (!mapView) return;

    const line = overettLinje.geometry as Polyline;
    const start = line.paths[0][0];
    const end = line.paths[0][line.paths[0].length - 1];

    let endPoint = null;

    const point1 = new Point({
        x: start[0],
        y: start[1],
        spatialReference: mapView.spatialReference,
    });
    const point2 = new Point({
        x: end[0],
        y: end[1],
        spatialReference: mapView.spatialReference,
    });

    const distance1 = point1.distance(overettUpper);
    const distance2 = point2.distance(overettUpper);

    if (distance1 < distance2) {
        endPoint = point2;
    } else {
        endPoint = point1;
    }

    return endPoint;
};
